import { useState, useEffect, useRef, useCallback } from 'react';
import LoginPage from './pages/LoginPage';
import AdminApp from './components/AdminApp';
import { setToken, setUnauthorizedHandler } from './auth';

const SESSION_KEY = 'admin_session';
const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30분
const REFRESH_THROTTLE_MS = 4 * 60 * 1000; // 활동 시 최대 4분마다 토큰 갱신
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
const REFRESH_URL = '/api/accounts/refresh';

/** 저장된 세션 복원 (30분 유휴 초과 시 만료 처리) */
function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || !s.account || !s.token || !s.lastActivity) return null;
    if (Date.now() - s.lastActivity > IDLE_LIMIT_MS) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s;
  } catch (_) {
    return null;
  }
}

function App() {
  const [session, setSession] = useState(loadSession); // { account, token } | null
  const [kickMsg, setKickMsg] = useState(null);        // 강제 로그아웃 사유 메시지
  const idleTimer = useRef(null);
  const lastWrite = useRef(0);
  const lastRefresh = useRef(0);

  const account = session ? session.account : null;
  const token = session ? session.token : null;

  const persist = useCallback((account, token, ts) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ account, token, lastActivity: ts }));
  }, []);

  const logout = useCallback((reason) => {
    localStorage.removeItem(SESSION_KEY);
    setToken(null);
    setSession(null);
    if (reason) setKickMsg(reason);
  }, []);

  const login = useCallback((data) => {
    // data = { token, account }
    const acc = data.account;
    const safe = {
      intNum: acc.intNum,
      strID: acc.strID,
      charName: acc.charName,
      intPermission: acc.intPermission,
    };
    persist(safe, data.token, Date.now());
    setToken(data.token);
    setSession({ account: safe, token: data.token });
  }, [persist]);

  // 모듈 토큰/401 핸들러를 자식 렌더 이전에 동기 설정
  setToken(token);
  setUnauthorizedHandler(logout);

  // 로그인 상태에서만: 활동 추적 + 30분 유휴 자동 로그아웃 + 활동 시 토큰 갱신
  useEffect(() => {
    if (!session) return undefined;

    const resetTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(logout, IDLE_LIMIT_MS);
    };

    const refreshToken = async () => {
      try {
        const res = await fetch(REFRESH_URL, {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.token}` },
        });
        if (res.status === 401) {
          logout();
          return;
        }
        if (res.ok) {
          const body = await res.json();
          if (body.token) {
            setToken(body.token);
            persist(session.account, body.token, Date.now());
            setSession((s) => (s ? { ...s, token: body.token } : s));
          }
        }
      } catch (_) {
        /* 네트워크 오류 시 다음 활동에 재시도 */
      }
    };

    const onActivity = () => {
      const now = Date.now();
      resetTimer();
      if (now - lastWrite.current > 30000) {
        lastWrite.current = now;
        persist(session.account, session.token, now);
      }
      if (now - lastRefresh.current > REFRESH_THROTTLE_MS) {
        lastRefresh.current = now;
        refreshToken();
      }
    };

    resetTimer();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [session, logout, persist]);

  if (!account) {
    return <LoginPage onLogin={(data) => { setKickMsg(null); login(data); }} kickMessage={kickMsg} />;
  }
  return <AdminApp account={account} onLogout={logout} />;
}

export default App;
